/**
 * Authenticated layout for every gated route.
 *
 * Top bar with the wordmark + global search affordance, sidebar with grouped
 * navigation, and a content well that the route renders into. The sidebar
 * collapses to a top tab bar on small screens.
 */

import { type ReactNode } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import {
  LineChart,
  Filter,
  Calculator,
  ListChecks,
  History,
  Activity,
  Layers,
  Users,
  MessageSquare,
  Home,
  CreditCard,
  Info,
  Mail,
  Sparkles,
  LogOut,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuthStore } from "@/lib/auth";
import { cn } from "@/lib/utils";
import logoUrl from "/brand/logo.png";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LineChart;
}

const TOOL_NAV: NavItem[] = [
  { to: "/app/options", label: "Options", icon: Layers },
  { to: "/app/option-chart", label: "Option Chart", icon: LineChart },
  { to: "/app/screener", label: "Screener", icon: Filter },
  { to: "/app/grid", label: "Grid", icon: ListChecks },
  { to: "/app/calculator", label: "Calculator", icon: Calculator },
  { to: "/app/watchlist", label: "Watchlist", icon: Activity },
  { to: "/app/volatility", label: "Volatility", icon: Sparkles },
  { to: "/app/option-history", label: "History", icon: History },
];

const COMMUNITY_NAV: NavItem[] = [
  { to: "/app/members", label: "Members", icon: Users },
  { to: "/app/forums", label: "Forums", icon: MessageSquare },
];

const SITE_NAV: NavItem[] = [
  { to: "/app", label: "Home", icon: Home },
  { to: "/app/pricing", label: "Pricing", icon: CreditCard },
  { to: "/app/about", label: "About", icon: Info },
  { to: "/app/contact", label: "Contact", icon: Mail },
];

function SidebarLink({ to, label, icon: Icon }: NavItem) {
  return (
    <NavLink
      to={to}
      end={to === "/app"}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2 text-sm font-medium transition-colors",
          isActive
            ? "bg-brand-500/15 text-brand-200"
            : "text-ink-400 hover:bg-ink-800/60 hover:text-ink-100",
        )
      }
    >
      <Icon className="size-4" />
      <span>{label}</span>
    </NavLink>
  );
}

function NavSection({ title, items }: { title: string; items: NavItem[] }) {
  return (
    <div>
      <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-500">
        {title}
      </div>
      <nav className="flex flex-col gap-0.5">
        {items.map((item) => (
          <SidebarLink key={item.to} {...item} />
        ))}
      </nav>
    </div>
  );
}

export function AppShell() {
  const lock = useAuthStore((s) => s.lock);

  return (
    <div className="min-h-screen bg-ink-950 text-ink-100">
      {/* Background glow accents */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 left-1/4 size-96 rounded-full bg-brand-500/10 blur-[120px]" />
        <div className="absolute right-1/4 top-2/3 size-96 rounded-full bg-brand-400/5 blur-[120px]" />
      </div>

      <div className="mx-auto flex max-w-[1600px] gap-8 px-4 py-6 lg:px-8">
        {/* Sidebar */}
        <aside className="sticky top-6 hidden h-[calc(100vh-3rem)] w-60 shrink-0 flex-col gap-6 rounded-[var(--radius-lg)] border border-ink-800/80 bg-ink-900/50 p-4 backdrop-blur-md lg:flex">
          <Link to="/app" className="px-2">
            <img
              src={logoUrl}
              alt="IntraAlpha"
              className="h-7 w-auto mix-blend-screen"
              draggable={false}
            />
          </Link>

          <div className="flex flex-1 flex-col gap-6 overflow-y-auto">
            <NavSection title="Tools" items={TOOL_NAV} />
            <NavSection title="Community" items={COMMUNITY_NAV} />
            <NavSection title="Site" items={SITE_NAV} />
          </div>

          <button
            onClick={lock}
            className="flex items-center gap-2 rounded-[var(--radius-md)] border border-ink-800 bg-ink-900/60 px-3 py-2 text-sm text-ink-400 hover:border-ink-700 hover:text-ink-100 transition-colors"
          >
            <LogOut className="size-4" />
            Sign out of preview
          </button>
        </aside>

        {/* Main content */}
        <main className="min-w-0 flex-1">
          {/* Mobile top bar */}
          <header className="sticky top-0 z-20 mb-6 flex items-center justify-between gap-3 rounded-[var(--radius-lg)] border border-ink-800/80 bg-ink-900/70 px-4 py-3 backdrop-blur-md lg:hidden">
            <Link to="/app">
              <img
                src={logoUrl}
                alt="IntraAlpha"
                className="h-6 w-auto mix-blend-screen"
                draggable={false}
              />
            </Link>
            <button
              onClick={lock}
              aria-label="Sign out"
              className="text-ink-400 hover:text-ink-100"
            >
              <LogOut className="size-4" />
            </button>
          </header>

          <motion.div
            key={location.pathname /* re-trigger entry per route */}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
}

/** Common page wrapper used by every gated route. */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow && (
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-brand-300">
            {eyebrow}
          </div>
        )}
        <h1 className="font-display text-3xl font-bold tracking-tight text-ink-50 sm:text-4xl">
          {title}
        </h1>
        {description && (
          <p className="mt-2 max-w-2xl text-sm text-ink-400">{description}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
